/**
 * Custom Hook: usePaginatedList
 * Skill: pagination-tinvaz.skill.md
 * 
 * Hook genérico para gestión de listados paginados con:
 * - Paginación
 * - Filtros
 * - Caché con localStorage (opcional)
 * - Invalidación automática vía Socket.io (opcional)
 * - Estados de loading y error
 * 
 * @module hooks/usePaginatedList
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './useAuth';
import { useCache } from './useCache';
import { paginate } from '../api/paginate';

/**
 * Hook para gestión completa de una lista paginada
 * 
 * @param {Object} options - Configuración
 * @param {string} options.endpoint - Endpoint de la API (ej: '/api/Clientes/paginar')
 * @param {Object} [options.initialFilters={}] - Filtros iniciales
 * @param {number} [options.initialPerPage=20] - Items por página inicial
 * @param {Object} [options.sort={_id: -1}] - Ordenamiento
 * @param {boolean} [options.enableCache=false] - Habilitar caché
 * @param {number} [options.cacheTTL=1200000] - TTL del caché en ms (20 min default)
 * @param {string} [options.socketEvent] - Evento Socket.io para invalidación
 * @param {Function} [options.buildConditions] - Función personalizada para construir query
 * @param {boolean} [options.autoLoad=true] - Cargar automáticamente al montar
 * 
 * @returns {Object} Estado y funciones de control
 */
export const usePaginatedList = ({
  endpoint,
  initialFilters = {},
  initialPerPage = 20,
  sort = { _id: -1 },
  enableCache = false,
  cacheTTL = 1200000,
  socketEvent,
  buildConditions: customBuildConditions,
  autoLoad = true
} = {}) => {
  // Validación
  if (!endpoint) {
    throw new Error('usePaginatedList: endpoint es requerido');
  }

  const { userData } = useAuth();

  // Ref-stabilize sort and customBuildConditions to avoid infinite loops
  // when consumers pass inline objects/functions (new reference each render)
  const sortRef = useRef(sort);
  if (JSON.stringify(sort) !== JSON.stringify(sortRef.current)) {
    sortRef.current = sort;
  }
  const customBuildConditionsRef = useRef(customBuildConditions);
  customBuildConditionsRef.current = customBuildConditions;

  // Hook de cache (solo se usa si enableCache es true)
  const cacheNamespace = useMemo(() => {
    // Generar namespace basado en el endpoint
    const cleanEndpoint = endpoint.replace(/^\/api\//, '').replace(/\/paginar$/, '');
    return cleanEndpoint.toLowerCase();
  }, [endpoint]);
  
  const cacheHook = useCache({
    namespace: cacheNamespace,
    ttl: cacheTTL,
    socketEvent: enableCache ? socketEvent : undefined,
    enabled: enableCache
  });

  // Estados consolidados
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(initialPerPage);
  const [filters, setFilters] = useState(initialFilters);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  /**
   * Construir condiciones de consulta
   * Usa función personalizada si se provee, sino usa limpieza básica
   */
  const buildConditions = useCallback(() => {
    // Si hay función custom, usarla (via ref para estabilidad)
    if (customBuildConditionsRef.current) {
      return customBuildConditionsRef.current(filters);
    }
    
    // Limpieza default: eliminar valores vacíos
    return Object.fromEntries(
      Object.entries(filters).filter(([, v]) => 
        v !== '' && v !== null && v !== undefined
      )
    );
  }, [filters]);

  /**
   * Cargar datos (con caché opcional)
   */
  // Extraer funciones estables del cacheHook para evitar que el objeto
  // contenedor (nueva referencia cada render) desestabilice loadData.
  const { getCached, setCached } = cacheHook;

  const loadData = useCallback(async () => {
    if (!userData && autoLoad) return;
    
    setLoading(true);
    setError(null);

    try {
      const conditions = buildConditions();
      const cacheKey = { page, filters, perPage };

      // Intentar obtener desde caché (si está habilitado)
      if (enableCache) {
        const cached = getCached(cacheKey);
        if (cached) {
          setData(cached);
          setLoading(false);
          return;
        }
      }

      // Consultar API
      const response = await paginate(
        `${endpoint}?page=${page}&perPage=${perPage}`,
        {
          conditions,
          sort: sortRef.current
        }
      );
      
      if (response?.data?.data) {
        const rawData = response.data.data;
        
        // Mapear estructura del backend (snake_case) a camelCase
        const paginatedData = {
          results: rawData.results || [],
          page: rawData.page || page,
          perPage: rawData.perPage || perPage,
          total: rawData.total_elements || 0,
          totalPages: rawData.total_pages || 0,
          hasNextPage: !!rawData.next,
          hasPrevPage: !!rawData.prev,
          next: rawData.next,
          prev: rawData.prev
        };
        
        setData(paginatedData);
        
        // Guardar en caché (si está habilitado)
        if (enableCache) {
          setCached(paginatedData, cacheKey);
        }
      } else {
        throw new Error('Respuesta inválida de la API');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Error al cargar datos';
      setError(errorMessage);
      console.error('Error en usePaginatedList:', err);
    } finally {
      setLoading(false);
    }
  }, [userData, page, filters, perPage, buildConditions, endpoint, enableCache, getCached, setCached, autoLoad]);

  /**
   * Efecto para cargar datos cuando cambian dependencias
   */
  useEffect(() => {
    if (autoLoad) {
      loadData();
    }
  }, [loadData, refreshTrigger, autoLoad]);

  /**
   * Forzar recarga de datos (bypass caché)
   */
  const { clearPendingUpdates, clearCache: clearCacheFn } = cacheHook;

  const refresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
    if (enableCache) {
      clearPendingUpdates();
    }
  }, [enableCache, clearPendingUpdates]);

  /**
   * Cargar datos manualmente (útil cuando autoLoad=false)
   */
  const load = useCallback(() => {
    loadData();
  }, [loadData]);

  /**
   * Cambiar página
   */
  const changePage = useCallback((newPage) => {
    if (newPage < 1 || newPage > (data.totalPages || 1)) return;
    setData({}); // Limpiar datos mientras carga (loading optimista)
    setPage(newPage);
  }, [data.totalPages]);

  /**
   * Actualizar múltiples filtros (reinicia a página 1)
   */
  const updateFilters = useCallback((newFilters) => {
    setPage(1);
    setData({}); // Limpiar datos
    setFilters(prevFilters => ({
      ...prevFilters,
      ...newFilters
    }));
  }, []);

  /**
   * Actualizar un solo filtro (reinicia a página 1)
   */
  const updateFilter = useCallback((name, value) => {
    setPage(1);
    setData({}); // Limpiar datos
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value
    }));
  }, []);

  /**
   * Reiniciar filtros (reinicia a página 1)
   */
  const resetFilters = useCallback(() => {
    setPage(1);
    setData({}); // Limpiar datos
    setFilters(initialFilters);
  }, [initialFilters]);

  /**
   * Reiniciar a página 1 manteniendo filtros
   */
  const resetPage = useCallback(() => {
    setPage(1);
    setData({}); // Limpiar datos
  }, []);

  /**
   * Cambiar cantidad de items por página (vuelve a página 1)
   */
  const changePerPage = useCallback((newPerPage) => {
    setPage(1);
    setData({}); // Limpiar datos
    setPerPage(newPerPage);
  }, []);

  /**
   * Limpiar cache completamente
   */
  const clearCache = useCallback(() => {
    if (enableCache) {
      clearCacheFn();
    }
  }, [enableCache, clearCacheFn]);

  // Retornar API pública del hook
  return {
    // Datos
    data,
    results: data.results || [],
    pagination: {
      page: data.page || page,
      perPage: data.perPage || perPage,
      total: data.total || 0,
      totalPages: data.totalPages || 0,
      hasNextPage: data.hasNextPage || false,
      hasPrevPage: data.hasPrevPage || false
    },
    
    // Estados
    loading,
    error,
    
    // Filtros
    filters,
    updateFilters,
    updateFilter,
    resetFilters,
    
    // Paginación
    page,
    changePage,
    resetPage,
    perPage,
    changePerPage,
    
    // Acciones
    refresh,
    load,
    clearCache,
    
    // Indicadores de caché (solo si cache habilitado)
    pendingUpdates: enableCache ? cacheHook.pendingUpdates : 0,
    hasPendingUpdates: enableCache ? cacheHook.pendingUpdates > 0 : false
  };
};

export default usePaginatedList;
